// Hooks against the duo_pack_formats registry (mig 20260703040000, G-08).
//
// A DUO pack is a punnet holding TWO fruit commodities at a fixed split
// (pct_a / derived pct_b = 100 - pct_a). Registered per tenant. Drives the
// two-component pallet_lines auto-split at pack time (each line carries its
// component commodity_id per the mig-20260703030000 read contract) and the
// one-SALES-Duo revenue mapping (Phase 4). A DUO is a PACK FORMAT, never a
// commodity — see docs/specs/packhouse-duo-mixed-commodity-pallet.md.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface DuoPackFormat {
  duo_format_id:  string
  tenant_id:      string
  code:           string
  label:          string | null
  size_code:      string | null
  commodity_a_id: string
  commodity_b_id: string
  // NUMERIC(5,2). pct_b is GENERATED (100 - pct_a) in the DB — single source of
  // truth for the ratio, so it can never sum to ≠ 100.
  pct_a:          number
  pct_b:          number
  is_active:      boolean
  sort_order:     number
  // Phase 4 (mig 20260703070000): the Pastel inventory group this pack's revenue
  // posts to as one SALES Duo line. NULL = unmapped.
  inventory_group_id: string | null
}

export interface InventoryGroup {
  inventory_group_id: string
  group_code:         string
  description:        string | null
}

// supabase-js only resolves a string-LITERAL select at the type level; a
// concatenated/variable select degrades to GenericStringError[]. Keep it inline.
const STALE = 5 * 60_000

// The active tenant's Pastel inventory groups (mig 830) — for the DUO pack's
// SALES-group picker. RLS scopes to the tenant; empty for tenants with no
// subledger seeded (the mapping is then simply left unset).
export function useInventoryGroups() {
  return useQuery({
    queryKey: ['inventory-groups'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_groups')
        .select('inventory_group_id, group_code, description')
        .eq('is_active', true)
        .order('group_code')
      if (error) throw error
      return (data ?? []) as InventoryGroup[]
    },
  })
}

// Active DUO pack formats for the current tenant — the pack-time picker.
// RLS scopes to the active tenant. Empty when the tenant has registered none
// (the packing flow simply offers no DUO option, exactly as today).
export function useDuoPackFormats() {
  return useQuery({
    queryKey: ['duo-pack-formats'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('duo_pack_formats')
        .select('duo_format_id, tenant_id, code, label, size_code, commodity_a_id, commodity_b_id, pct_a, pct_b, is_active, sort_order, inventory_group_id')
        .eq('is_active', true)
        .order('sort_order').order('code')
      if (error) throw error
      return (data ?? []) as DuoPackFormat[]
    },
  })
}

// Every DUO pack format incl. inactive — for the config registry editor only.
export function useDuoPackFormatsAdmin() {
  return useQuery({
    queryKey: ['duo-pack-formats-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('duo_pack_formats')
        .select('duo_format_id, tenant_id, code, label, size_code, commodity_a_id, commodity_b_id, pct_a, pct_b, is_active, sort_order, inventory_group_id')
        .order('sort_order').order('code')
      if (error) throw error
      return (data ?? []) as DuoPackFormat[]
    },
  })
}
